import { ApolloClient } from "@apollo/client";
import {
	createAsyncThunk,
	createSelector,
	createSlice,
	current,
	PayloadAction,
} from "@reduxjs/toolkit";
import { isArray } from "lodash";
import type { RootState } from "store";
import { filterAvailableDishTypes, sortDishTypes } from "utils/mealListUtils";
import { getAvailableDishTypeIds, mergeDishTypes } from "utils/utils";

import { LocalDishType, LocalFridgeDishes } from "types/mainTypes";

import { captureApolloError } from "../services/external/sentry";
import { Price } from "../types";
import { getFridgeId } from "../utils/browser/localStorage";
import { FETCH_DISHTYPE_IDS_AND_PRICES, FETCH_UNKNOWN_DISH_TYPE } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";
import { selectFilters, selectSelectedLanguage } from "./appStateSlice";
import { selectOrder, selectOrderDishes } from "./transactionSlice";

type DishesSlice = LocalFridgeDishes;

const initialState: DishesSlice = {
	types: [],
	items: [],
	unknownDishTypeIds: [],
};

type FetchUnknownDishTypesPayload = {
	orderDishTypes: LocalDishType[];
	apolloClient: ApolloClient<unknown>;
};

// Helper function to validate if a dish type has necessary fields for rendering
const isValidDishType = (dishType: LocalDishType): boolean => {
	return Boolean(dishType && dishType.id && dishType.description && dishType.price);
};

export const fetchUnknownDishTypes = createAsyncThunk(
	"dishes/fetchUnknownDishTypesOnTheFly",
	async (
		{ orderDishTypes, apolloClient }: FetchUnknownDishTypesPayload,
		{ getState, rejectWithValue },
	) => {
		const state = getState() as RootState;
		const currentDishTypeIds = new Set(state.dishes.types?.map((type) => type.id) || []);
		const fridgeId = getFridgeId();

		const unknownDishTypeIds = orderDishTypes
			.filter((dishType) => dishType?.id && !currentDishTypeIds.has(dishType.id))
			.map((dish) => dish.id)
			.filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates

		if (unknownDishTypeIds.length === 0 || !fridgeId) {
			return [];
		}

		try {
			logToSentry(
				`Fetching unknown dish types on the fly: ${unknownDishTypeIds.join(
					", ",
				)} for fridge: ${fridgeId}`,
				"info",
			);

			const results = await Promise.all(
				unknownDishTypeIds.map((dishTypeId) =>
					apolloClient
						.query({
							query: FETCH_UNKNOWN_DISH_TYPE,
							variables: { fridgeId, dishTypeId },
							fetchPolicy: "network-only",
						})
						.catch((error) => {
							captureApolloError(error, {
								kind: "Fetch_Unknown_Dish_Type_Error",
								query: FETCH_UNKNOWN_DISH_TYPE.kind,
								variables: { fridgeId, dishTypeId },
							});
							return null;
						}),
				),
			);

			const fetchedDishTypes = results
				.filter((result) => result?.data?.fridge?.dishes?.type)
				.map((result) => ({ ...result?.data.fridge.dishes.type }));

			logToSentry(
				`Successfully fetched ${fetchedDishTypes.length} unknown dish types for fridge: ${fridgeId}`,
				"info",
			);

			return fetchedDishTypes;
		} catch (error) {
			captureApolloError(error as Error, {
				kind: "Fetch_Unknown_Dish_Types_Batch_Error",
				query: FETCH_UNKNOWN_DISH_TYPE.kind,
				variables: { fridgeId, unknownDishTypeIds: unknownDishTypeIds.join(",") },
			});
			return rejectWithValue((error as Error).message);
		}
	},
);

export const refetchDishTypePrices = createAsyncThunk(
	"dishes/refetchDishTypePrices",
	async (apolloClient: ApolloClient<unknown>, { rejectWithValue }) => {
		const fridgeId = getFridgeId();

		if (!fridgeId) {
			return rejectWithValue("No fridge ID found");
		}

		try {
			const result = await apolloClient.query({
				query: FETCH_DISHTYPE_IDS_AND_PRICES,
				variables: { id: fridgeId },
				fetchPolicy: "network-only",
			});

			return result.data?.fridge?.dishes?.types || [];
		} catch (error) {
			captureApolloError(error as Error, {
				kind: "Refetch_Dish_Type_Prices_Error",
				query: FETCH_DISHTYPE_IDS_AND_PRICES.kind,
				variables: { id: fridgeId },
			});
			return rejectWithValue((error as Error).message);
		}
	},
);

export const dishesSlice = createSlice({
	name: "dishes",
	initialState,
	reducers: {
		setInitialDishTypes: (state, action) => {
			state.types = action.payload;
		},
		setDishTypes: (
			state,
			{ payload }: PayloadAction<{ dishTypes: LocalDishType[] | null; isOrder?: boolean }>,
		) => {
			const { dishTypes: updatedDishTypes, isOrder } = payload;

			if (!state.types) {
				if (updatedDishTypes) {
					state.types = updatedDishTypes;
				}
			} else if (isArray(updatedDishTypes)) {
				const updatedDishTypesIds = updatedDishTypes?.map((dishType) => dishType.id);

				// Update local dishes based on dishes array in WS message
				// Only update dish type dishes when the message is related to order
				if (isOrder) {
					const cleanedDishTypes = state.types?.map((dishType) => {
						const stateDishTypeShouldHaveEmptyDishes =
							dishType.dishes &&
							dishType.dishes?.length > 0 &&
							!updatedDishTypesIds?.includes(dishType.id);
						/* If local dish type has dishes, but it is not in updated dishes array it
							means, that last dish of that dish type was taken and we should set
							its dishes to empty array
						*/
						if (stateDishTypeShouldHaveEmptyDishes) {
							return {
								...dishType,
								dishes: [],
							};
							/*
							 * If given dish type is in updated dishes array,
							 * return update dish type from updated dishes array
							 * */
						} else if (updatedDishTypesIds?.includes(dishType.id)) {
							return updatedDishTypes.find(
								(updatedDishType) => updatedDishType.id === dishType.id,
							);
						}

						/*
						 * else return original dish type
						 * */
						return dishType;
					});
					state.types = [...mergeDishTypes(current(state.types), cleanedDishTypes)];
				} else {
					state.types = [...mergeDishTypes(current(state.types), updatedDishTypes)];
				}
			} else {
				if (updatedDishTypes) {
					state.types = [...mergeDishTypes(current(state.types), updatedDishTypes)];
				}
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				fetchUnknownDishTypes.fulfilled,
				(state, action: { payload: LocalDishType[] }) => {
					const fetchedDishTypes = action.payload;

					if (fetchedDishTypes.length > 0) {
						// Filter out incomplete dish types
						// that don't have necessary fields for rendering
						const validDishTypes = fetchedDishTypes.filter(isValidDishType);

						if (validDishTypes.length > 0) {
							// Add only the valid fetched dish types to the existing types
							if (state.types) {
								state.types = [
									...mergeDishTypes(current(state.types), [...validDishTypes]),
								];
							} else {
								state.types = [...validDishTypes];
							}

							// Clear the unknown dish type IDs
							// that were successfully fetched and validated
							const validFetchedIds = validDishTypes.map((dishType) => dishType.id);
							state.unknownDishTypeIds = state.unknownDishTypeIds.filter(
								(id) => !validFetchedIds.includes(id),
							);

							// Log successful validation
							logToSentry(
								`Added ${validDishTypes.length} valid dish types out of ${fetchedDishTypes.length} fetched`,
								"info",
							);
						}

						// Log if some dish types were filtered out due to incomplete data
						if (validDishTypes.length < fetchedDishTypes.length) {
							const invalidCount = fetchedDishTypes.length - validDishTypes.length;
							logToSentry(
								`Filtered out ${invalidCount} incomplete dish types that lack necessary fields for rendering`,
								"warning",
							);
						}
					}
				},
			)
			.addCase(fetchUnknownDishTypes.rejected, (state, action) => {
				logToSentry("Failed to fetch unknown dish types on the fly", "error", [
					{ payload: JSON.stringify(action.payload) },
				]);
			})
			.addCase(
				refetchDishTypePrices.fulfilled,
				(state, action: PayloadAction<{ id: string; price: Price }[] | null>) => {
					const updatedPrices = action.payload;
					if (state.types && updatedPrices?.length) {
						const priceMap = new Map(
							updatedPrices.map((priceData) => [priceData.id, priceData.price]),
						);

						state.types.forEach((dishType, index) => {
							const updatedPrice = priceMap.get(dishType.id);
							if (updatedPrice) {
								state.types![index].price = updatedPrice;
							}
						});
					}
				},
			)
			.addCase(refetchDishTypePrices.rejected, (state, action) => {
				logToSentry("Failed to refetch dish type prices", "error", [
					{ payload: JSON.stringify(action.payload) },
				]);
			});
	},
});

export const { setDishTypes, setInitialDishTypes } = dishesSlice.actions;

export const selectDishTypes = createSelector(
	[(state: RootState) => state.dishes.types],
	(dishTypes) => {
		// We want to return only dishes with price and description
		// that secures that we have the most important data for all the displayed dish types
		return dishTypes?.filter((dishType) => isValidDishType(dishType)) || [];
	},
);

export const selectAvailableDishTypesIds = createSelector(
	selectDishTypes,
	selectOrder,
	selectOrderDishes,
	(fridgeDishTypes, order, orderDishes) => {
		if (!fridgeDishTypes) {
			return new Set<string>();
		}
		return getAvailableDishTypeIds(fridgeDishTypes, orderDishes, order?.state);
	},
);
export const selectIsFridgeEmpty = createSelector(
	selectDishTypes,
	selectOrder,
	(fridgeDishTypes, order) => {
		const hasDishTypeWithDishes =
			fridgeDishTypes.length > 0 &&
			fridgeDishTypes.some((dishType) => Boolean(dishType.dishes?.length));

		return !order && !hasDishTypeWithDishes;
	},
);
export const selectAvailableDishTypes = createSelector(
	selectDishTypes,
	selectAvailableDishTypesIds,
	selectFilters,
	selectSelectedLanguage,
	(fridgeDishTypes, availableDishTypeIds, filters, selectedLanguage) => {
		const filteredDishes = filterAvailableDishTypes(
			fridgeDishTypes,
			availableDishTypeIds,
			filters.allergens,
			true,
		);
		return sortDishTypes(filteredDishes, selectedLanguage);
	},
);
export const selectDishTypesNotIncludedInFilters = createSelector(
	selectDishTypes,
	selectAvailableDishTypesIds,
	selectFilters,
	selectSelectedLanguage,
	(fridgeDishTypes, availableDishTypeIds, filters, selectedLanguage) => {
		const filteredDishes = filterAvailableDishTypes(
			fridgeDishTypes,
			availableDishTypeIds,
			filters.allergens,
			false,
		);
		return sortDishTypes(filteredDishes, selectedLanguage);
	},
);

export default dishesSlice.reducer;
